import { useCallback, type ReactNode } from 'react';
import type { LocaleCode } from './core';
import { useI18n } from './I18nProvider';

const zhCNVisibleCopy: Record<string, string> = {
  'Config editor foundation': '配置编辑基础',
  'Saving…': '保存中…',
  required: '必填',
  On: '开启',
  Off: '关闭',
  'Add row': '新增行',
  'Add key/value': '新增键值',
  Key: '键',
  Value: '值',
  Remove: '移除',
  'Select a secret reference': '选择 secret 引用',
  'Secret values are never rendered in Console v2; only stable reference IDs are saved.': 'Console v2 从不渲染 secret 值；只保存稳定引用 ID。',
  Close: '关闭',
  live: '实时',
  Live: '实时',
  redacted: '已脱敏',
  structured: '结构化',
  preview: '预览',
  wizard: 'wizard',
  'Draft JSON': '草稿 JSON',
  'Draft JSON / diff': '草稿 JSON / diff',
  'Draft JSON + diff': '草稿 JSON + diff',
  'View draft JSON + diff': '查看草稿 JSON + diff',
  'Unsaved structured changes': '未保存的结构化变更',
  'Unsaved Skill draft': '未保存的 Skill 草稿',
  'Preview Save draft': '预览保存草稿',
  'Draft vs published diff': '草稿与已发布版本 diff',
  'No draft changes compared with the published/current fallback.': '与已发布/当前兜底配置相比没有草稿变更。',
  Path: '路径',
  Change: '变更',
  'Published/current': '已发布/当前',
  Draft: '草稿',
  'Save draft': '保存草稿',
  'Preview draft': '预览草稿',
  'Publish version': '发布版本',
  'Rollback published pointer': '回滚已发布指针',
  'Activate skill': '启用 Skill',
  'Disable skill': '停用 Skill',
  'Archive skill': '归档 Skill',
  'Delete skill': '删除 Skill',
  'Discard changes': '放弃更改',
  Saved: '已保存',
  'Unsaved changes': '未保存更改',
  'Prefill source': '预填来源',
  'current draft': '当前草稿',
  'published config': '已发布配置',
  'current runtime config': '当前运行时配置',
  'empty defaults': '空默认值',

  Overview: '概览',
  Evidence: '证据',
  JSON: 'JSON',
  Actions: '操作',
  Action: '操作',
  Kind: '类型',
  Source: '来源',
  Status: '状态',
  State: '状态',
  Mode: '模式',
  Name: '名称',
  Description: '描述',
  Version: '版本',
  Versions: '版本',
  Runtime: '运行时',
  Session: '会话',
  Events: '事件',
  Persona: 'Persona',
  Personas: 'Personas',
  Platform: '平台',
  Platforms: '平台',
  Skill: 'Skill',
  Skills: '技能管理',
  Agent: 'Agent',
  Artifact: '工件',
  'Artifact ID': '工件 ID',
  Hash: 'Hash',
  Thread: '线程',
  Time: '时间',
  Actor: '操作者',
  Resource: '资源',
  'Resource kind': '资源类型',
  'Resource id': '资源 ID',
  Timestamp: '时间戳',
  'Event ID': '事件 ID',
  'State change': '状态变更',
  'Session key': '会话 key',
  'Runtime status': '运行时状态',
  Rows: '行数',
  'JSON detail': 'JSON 详情',
  'Detail lookup': '详情查询',
  'Explicit tab': '显式标签页',
  'Current page': '当前页',
  List: '列表',
  Clear: '清除',
  Unknown: '未知',
  unknown: '未知',
  Missing: '缺失',
  Unauthorized: '未授权',
  Forbidden: '无权限',
  Unavailable: '不可用',
  Enabled: '已启用',
  Disabled: '已禁用',
  Partial: '部分',
  Required: '必需',
  Optional: '可选',
  Yes: '是',
  No: '否',
  yes: '是',
  no: '否',
  none: '无',
  None: '无',
  current: '当前',
  behind: '落后',
  missing: '缺失',
  valid: '有效',
  materialized: '已物化',
  not_materialized: '未物化',
  version_drift: '版本不一致',
  draft_only: '仅草稿',
  archived_in_use: '已归档但仍被使用',
  stale_live: '实时加载已过期',
  orphaned: '孤立引用',
  Always: '始终',
  Inherit: '继承',
  Low: '低',
  Medium: '中',
  High: '高',
  'X-High': '超高',
  Default: '默认',
  Concise: '简洁',
  Quiet: '安静',
  'App token': 'App token',
  'User token': '用户 token',
  Error: '错误',
  'Not Found': '未找到',
  Paused: '已暂停',
  Failed: '失败',
  Released: '已发布',
  Active: '活跃',
  Pending: '待处理',
  paused: '已暂停',
  failed: '失败',
  released: '已发布',
  active: '活跃',
  archived: '已归档',
  configured: '已配置',
  disabled: '已停用',
  draft: '草稿',
  managed: '托管',
  native: 'Native',
  pending: '待处理',
  project: '项目',
  global: '全局',
  review: '审核中',
  'in-progress': '进行中',
  Parents: '父级 Engagement',
  Children: '子级 Engagement',
  Siblings: '同级 Engagement',
  parents: '父级 Engagement',
  children: '子级 Engagement',
  siblings: '同级 Engagement',
  'No parent engagements recorded.': '未记录父级 Engagement。',
  'No child engagements recorded.': '未记录子级 Engagement。',
  'No sibling engagements recorded.': '未记录同级 Engagement。',
  'Fallback to app': '回退到 App',
  pause: '暂停',
  resume: '恢复',
  skip: '跳过',
  'Management API': '管理 API',
  'Status + static evidence': '状态 + 静态证据',
  'Read-only': '只读',
  'Guarded read-only': '受保护只读',
  'Structured draft': '结构化草稿',

  Engagements: 'Engagements',
  Sessions: '会话',
  Audit: '审计',
  'Contract gaps': '契约缺口',
  Timeline: '时间线',
  'SSE stream': 'SSE 流',
  Controls: '控制',
  Exports: '导出',
  'Test message': '测试消息',
  'Messages / history': '消息 / 历史',
  'Prompt refs': 'Prompt 引用',
  'current / behind / missing': '当前 / 落后 / 缺失',
  'hash/ref missing': '缺少 Hash/ref',
  'Audit rows': '审计行',
  'Audit History': '审计历史',
  'Runtime Audit': '运行时审计',
  'Runtime Operations': '运行时操作',
  'Operational evidence and controls for engagements, sessions, state machine projections, live timelines, exports, audit, and controlled test messages.': 'Engagement、会话、状态机投影、实时事件线、导出、审计和受控测试消息的操作证据与控制。',
  'Runtime evidence + controls': '运行时证据 + 控制',
  'Live /api/v1/engagements rows': '实时 /api/v1/engagements 行',
  'Health badges from state + live sessions': '来自状态与实时会话的健康标记',
  'SessionRuntimeProfile-capable /api/v1/sessions': '支持 SessionRuntimeProfile 的 /api/v1/sessions',
  'Control-plane audit events': '控制平面审计事件',
  'No runtime rows returned.': '未返回运行时行。',
  'Runtime operations appear after engagements or sessions exist. Contract gaps are still shown so unsupported capabilities remain explicit.': '创建 Engagement 或会话后会显示运行时操作。契约缺口仍会展示，确保不支持的能力明确可见。',
  'Backend mutation endpoints for pause/resume/skip do not enforce explicit confirmation; Console v2 requires preview, reason, and typed confirmation before sending the mutation.': 'pause/resume/skip 后端变更接口尚未强制显式确认；Console v2 会在发送变更前要求预览、原因和输入确认。',
  'Pause/resume backend metadata still marks reason optional; Console v2 treats reason as required operational evidence.': 'pause/resume 后端 metadata 仍将原因标记为可选；Console v2 将原因视为必需的操作证据。',
  'Controlled test message has no dedicated Runtime preview/audit endpoint; Console v2 performs a local target preview before calling /personas/{persona}/send.': '受控测试消息尚无专用 Runtime 预览/审计接口；Console v2 会在调用 /personas/{persona}/send 前执行本地目标预览。',
  'SSE event stream is summary-only; payload/artifact details are fetched from /api/v1/events timeline rows.': 'SSE 事件流仅提供摘要；payload/工件详情会从 /api/v1/events 时间线行获取。',
  'Runtime audit has no direct /api/v1/audit/{id} lookup; detail is resolved from the current audit page.': 'Runtime 审计尚无直接 /api/v1/audit/{id} 查询；详情会从当前审计页面解析。',
  'Credential reveal-token support is not exposed in Runtime contracts yet; secret/token/credential fields remain redacted.': 'Runtime 契约尚未暴露凭据 reveal-token 支持；secret/token/credential 字段会保持脱敏。',
  'Runtime Profile': '运行时配置档',
  'Session Detail': '会话详情',
  'Runtime evidence': '运行时证据',
  'Runtime engagement': '运行时 Engagement',
  'Runtime session': '运行时会话',
  'Runtime audit': '运行时审计',
  'Audit event': '审计事件',
  'Runtime profile evidence': '运行时配置档证据',
  'Missing SessionRuntimeProfile': '缺少 SessionRuntimeProfile',
  'Engagement Operations': 'Engagement 操作',
  'Filters, health/state badges, and links into state machine, live stream, exports, controls, and test-message guards.': '筛选、健康/状态标记，以及进入状态机、实时流、导出、控制和测试消息保护路径的链接。',
  'Messages/history, runtime profile, provider/model/thread, and prompt composition evidence.': '消息/历史、运行时配置档、提供商/模型/线程，以及 Prompt 组合证据。',
  'Runtime Audit History': '运行时审计历史',
  'Audit rows are readable first; JSON details are available from an explicit tab.': '优先展示可读审计行；JSON 详情通过显式标签页打开。',
  'Operational center for relationships, state machine projection, event timeline/SSE, guarded controls, exports, and controlled test messages.': '关系、状态机投影、事件时间线/SSE、受保护控制、导出和受控测试消息的操作中心。',
  'Relationship Cards': '关系卡片',
  'Explicit parent/child/sibling links from EngagementControls metadata.': '来自 EngagementControls 元数据的显式父/子/同级链接。',
  'State Machine': '状态机',
  'Projection, axes, and action availability from backend contracts.': '来自后端契约的投影、坐标轴和操作可用性。',
  'Axes': '坐标轴',
  'Contract gap: axes unavailable': '契约缺口：坐标轴不可用',
  'Event Timeline': '事件时间线',
  'Structured payload/artifact summary first; JSON opens only from each event card.': '优先展示结构化载荷/工件摘要；JSON 仅从事件卡片打开。',
  'Session messages/history, runtime profile, provider/model/thread, and prompt composition evidence inside the token-protected console.': '在令牌保护的控制台中查看会话消息/历史、运行时配置档、提供商/模型/线程和 Prompt 组合证据。',
  'Runtime Relationships': '运行时关系',
  'Prompt Composition Evidence': 'Prompt 组合证据',
  'Token-protected Console v2 may show prompt/module evidence; credential-like fields stay redacted.': '令牌保护的 Console v2 可展示 Prompt/module 证据；类似凭据的字段保持脱敏。',
  'Messages / History': '消息 / 历史',
  'Message content is visible in Console v2; secret/token-like substrings are still redacted.': 'Console v2 可查看消息内容；类似 secret/token 的片段仍会脱敏。',
  'Runtime profile, provider/model/thread, and prompt references from the session contract.': '来自会话契约的运行时配置档、提供商/模型/线程和 Prompt 引用。',
  'Evidence relationships': '证据关系',
  'Prompt artifact refs': 'Prompt 工件引用',
  'Only artifact ids, versions, and hashes are shown.': '仅展示工件 ID、版本和 Hash。',
  'Runtime/control-plane audit history. Credential-like fields stay redacted until reveal-token support exists in the contract.': '运行时/控制平面审计历史；在契约支持 reveal-token 前，类似凭据的字段保持脱敏。',
  'No engagements match the filters.': '没有符合筛选条件的 Engagement。',
  'Adjust filters or wait for runtime activity.': '请调整筛选条件，或等待运行时活动。',
  Engagement: 'Engagement',
  'Health / state': '健康 / 状态',
  Updated: '更新时间',
  Created: '创建时间',
  'Runtime profile': '运行时配置档',
  'Provider/model': '提供商/模型',
  'Thread/status': '线程/状态',
  'Prompt evidence': 'Prompt 证据',
  'no session key': '无会话 key',
  'no persona snapshot': '无 Persona 快照',
  'artifact refs': '工件引用',
  'unknown state': '未知状态',
  'No events returned.': '未返回事件。',
  'The timeline appears when canonical runtime events exist for this engagement.': '当此 Engagement 存在规范运行时事件时，时间线会显示。',
  'unknown event': '未知事件',
  'unknown time': '未知时间',
  'unknown actor': '未知操作者',
  'Hide JSON': '隐藏 JSON',
  'View JSON': '查看 JSON',
  'No structured payload fields.': '无结构化载荷字段。',
  artifact: '工件',
  'missing ref': '缺少引用',
  'Connecting to /api/v1/events/stream…': '正在连接 /api/v1/events/stream…',
  'Connected: live SSE stream is open.': '已连接：实时 SSE 流已打开。',
  'SSE stream disconnected or unavailable.': 'SSE 流已断开或不可用。',
  'SSE / Live Event Stream': 'SSE / 实时事件流',
  'Guarded Controls': '受保护控制',
  'Preview, reason, and explicit typed confirmation are required in Console v2. Skip is submitted only when backend preview allows it.': 'Console v2 需要预览、原因和显式输入确认。仅当后端预览允许时才提交 skip。',
  'Operator ID': '操作者 ID',
  Reason: '原因',
  'Required operational reason': '必填操作原因',
  'Explicit confirmation': '显式确认',
  'Preview target transition': '预览目标状态转换',
  'Confirm and send': '确认并发送',
  'Control failed.': '控制失败。',
  'Contract gap:': '契约缺口：',
  'backend pause/resume/skip endpoints do not enforce explicit confirmation; this UI guard is intentionally stricter.': '后端 pause/resume/skip 接口尚未强制显式确认；此前端保护会刻意更严格。',
  'Allowed by backend preview': '后端预览允许',
  'Blocked by backend preview': '后端预览阻止',
  'Records event': '记录事件',
  'Event Exports': '事件导出',
  'Downloads are filtered to this engagement via /api/v1/events/export.': '下载内容会通过 /api/v1/events/export 按此 Engagement 筛选。',
  'Export NDJSON': '导出 NDJSON',
  'Export JSON': '导出 JSON',
  'Export CSV': '导出 CSV',
  Formats: '格式',
  'Controlled Test Message': '受控测试消息',
  'Target preview comes first, then explicit confirmation before /personas/{persona}/send.': '先进行目标预览，再在 /personas/{persona}/send 前显式确认。',
  'Guardrail:': '安全边界：',
  'this is a controlled operational tool. It cannot send until target preview is acknowledged and confirmation text matches exactly.': '这是受控操作工具。必须确认目标预览且确认文本完全匹配后才能发送。',
  'Contract gap: no session rows': '契约缺口：没有会话行',
  'Target session': '目标会话',
  Confirmation: '确认',
  Message: '消息',
  'Message to send to the selected runtime target': '发送到所选运行时目标的消息',
  'Target Preview': '目标预览',
  'Message preview': '消息预览',
  'No message entered': '未输入消息',
  'Acknowledge target preview': '确认目标预览',
  'Confirm and send test message': '确认并发送测试消息',
  'Send failed.': '发送失败。',
  'Test message sent.': '测试消息已发送。',
  'backend send endpoint has no dedicated Runtime preview/audit contract; Console v2 performs a local target preview guard.': '后端发送接口尚无专用 Runtime 预览/审计契约；Console v2 会执行本地目标预览保护。',
  'No audit rows returned.': '未返回审计行。',
  'Audit history appears when the control plane returns events.': '控制平面返回事件后会显示审计历史。',
  'No prompt module rows returned.': '未返回 Prompt module 行。',
  'The session API did not include effective prompt module evidence.': '会话 API 未包含有效 Prompt module 证据。',
  'No history entries returned.': '未返回历史条目。',
  'The session API returned no history rows for this session.': '会话 API 未返回此会话的历史行。',
  'No prompt artifact refs returned.': '未返回 Prompt 工件引用。',
  'Runtime prompt evidence remains unavailable until the API provides hash/artifact refs.': '在 API 提供 Hash/工件引用前，运行时 Prompt 证据不可用。',
  unnamed: '未命名',
  'missing artifact id': '缺少工件 ID',
  'missing hash': '缺少 Hash',
  'Backend Contract Gaps': '后端契约缺口',
  'Unsupported or partially supported backend capabilities are explicit instead of hidden.': '不支持或部分支持的后端能力会显式展示，而不是隐藏。',
  'Loading live runtime evidence…': '正在加载实时运行时证据…',
  'Partial live data unavailable.': '部分实时数据不可用。',
  'Loading live management API data…': '正在加载实时管理 API 数据…',
  'Partial/read-only data': '部分/只读数据',

  'Usage / consumers': '使用 / 消费方',
  'View Config JSON': '查看配置 JSON',
  'Creating Skill…': '正在创建 Skill…',
  'Skill created. Opening detail…': 'Skill 已创建，正在打开详情…',
  'Skill Library': '技能库',
  'Loaded Skills': '已加载技能',
  'Configured managed skill artifacts. Loaded Skills remains separate runtime evidence.': '已配置的托管 Skill 工件。已加载技能仍作为独立运行时证据展示。',
  'Usage evidence': '使用证据',
  Definitions: '定义',
  Conflicts: '冲突',
  Managed: '托管',
  'Partial/unknown': '部分/未知',
  'Partial/read-only': '部分/只读',
  'Unavailable data is explicit': '不可用数据会明确标记',
  'Configured artifact list': '已配置工件列表',
  'Live artifact list': '实时工件列表',
  'Managed definition': '托管定义',
  'Managed definitions': '托管定义',
  'Managed skill artifacts': '托管 Skill 工件',
  'Managed skill artifact detail with Markdown-first content and read-only usage evidence.': '托管 Skill 工件详情，包含 Markdown 优先内容和只读使用证据。',
  'Artifact manifest': '工件清单',
  'Draft revision': '草稿修订',
  'Current version': '当前版本',
  'Published version': '已发布版本',
  'Latest version': '最新版本',
  'Usage boundary': '使用边界',
  'Persona binding is read-only here; edit bindings in Persona config': '此处 Persona 绑定只读；请在 Persona config 中编辑绑定',
  '/installations evidence only; no persona/platform inference': '仅 /installations 证据；不推断 Persona/平台',
  'Live managed skill artifacts from /api/v1/skills. Persona binding remains in Persona config.': '来自 /api/v1/skills 的实时托管 Skill 工件。Persona 绑定保留在 Persona 配置中。',
  'No managed skills returned.': '未返回托管 Skill。',
  'The management API returned an empty Skill Library.': '管理 API 返回了空技能库。',
  'Runtime-backed skill visibility from live persona detail snapshots. This page does not infer exposure from configuration alone.': '来自实时 Persona 详情快照的运行时 Skill 可见性。此页面不会仅根据配置推断暴露范围。',
  'Runtime-loaded skills': '运行时加载的 Skill',
  'Exposure evidence': '暴露证据',
  'Live persona detail only': '仅实时 Persona 详情',
  'Live persona skill snapshots': '实时 Persona Skill 快照',
  'No live loaded skills returned.': '未返回实时加载的 Skill。',
  'Loaded skills appear only when live persona detail endpoints expose skill snapshots.': '只有实时 Persona 详情接口暴露 Skill 快照时，才会显示已加载 Skill。',
  Method: '方法',
  Endpoint: '接口',
  Validation: '校验',
  Changelog: '变更说明',
  Publish: '发布',
  Rollback: '回滚',
  published: '已发布',
  Layers: '层级',
  Navigation: '导航',
  'runtime loaded evidence present': '存在运行时加载证据',
  'runtime evidence not loaded': '运行时证据未加载',
  'Persona config': 'Persona 配置',
  'Draft JSON is available only in this drawer. The primary Skill editing path is structured fields; this view shows the current unsaved draft projection and redacted diff.': '草稿 JSON 仅在此抽屉中可用。Skill 主要编辑路径是结构化字段；此视图展示当前未保存草稿投影和脱敏 diff。',
  'Draft JSON drawer': '草稿 JSON 抽屉',
  'Published JSON': '已发布 JSON',
  'Create Skill artifact': '创建 Skill 工件',
  'Skill detail': '技能详情',
  'Artifact summary': '工件摘要',
  'Definition vs runtime evidence': '定义与运行时证据',
  'Managed artifact changes stay here; Persona binding remains in Persona config.': '托管工件变更保留在此；Persona 绑定仍在 Persona config 中。',
  'Draft, version, publish, rollback, archive, and delete actions open guarded drawers.': '草稿、版本、发布、回滚、归档和删除操作会打开受保护抽屉。',
  'Runtime-loaded evidence is read-only and linked from usage rows.': '运行时加载证据只读，并从使用行链接进入。',
  'Loaded Skills evidence': '已加载技能证据',
  'Bind, install, and unbind from Persona config, not from Skills.': '绑定、安装和解绑在 Persona config 中完成，而不是在 Skill 页面中。',
  'Version history': '版本历史',
  'Versions are immutable Skill artifact definitions; publishing changes the artifact pointer only.': '版本是不可变 Skill 工件定义；发布只改变工件指针。',
  'No versions returned.': '未返回版本。',
  'Create a version after saving and validating a draft.': '保存并校验草稿后创建版本。',
  'Read-only usage / consumers': '只读使用 / 消费方',
  'Navigate to Persona config or runtime evidence. Skill pages do not bind, install, or unbind Personas.': '请导航到 Persona config 或运行时证据。Skill 页面不绑定、安装或解绑 Persona。',
  'No consumers returned.': '未返回消费方。',
  'The reverse lookup found no Persona configs or runtime layers referencing this Skill.': '反向查询未找到引用此 Skill 的 Persona 配置或运行时层级。',
  'Edit Skill draft': '编辑 Skill 草稿',
  'Action result:': '操作结果：',
  'Skill lifecycle action': 'Skill 生命周期操作',
  'Skill lifecycle status': 'Skill 生命周期状态',
  'Skill action evidence': 'Skill 操作证据',
  'No action result returned yet.': '尚未返回操作结果。',
  'No mutation preview selected.': '未选择变更预览。',
  'Choose a Skill lifecycle control to inspect the exact API request first.': '请先选择一个 Skill 生命周期控制项以检查精确 API 请求。',
  'I reviewed the request preview and understand this action.': '我已检查请求预览并了解此操作。',
  Apply: '执行',
  'Run read-only action': '运行只读操作',
  'Backend/contract gap:': '后端/契约缺口：',
  'No tag rows configured.': '未配置标签行。',
  'No agent rows configured.': '未配置 Agent 行。',
  'No platform rows configured.': '未配置平台行。',
  'No arg rows configured.': '未配置参数行。',
  'Add tag': '新增标签',
  'Add agent': '新增 Agent',
  'Add platform': '新增平台',
  'Add arg': '新增参数',
  'Loaded skill': '已加载 Skill',
  'Runtime-backed skill evidence for a single persona. This does not infer platform/session exposure beyond live persona detail data.': '单个 Persona 的运行时 Skill 证据；不会超出实时 Persona 详情数据推断平台/会话暴露。',
  'Managed Skill definition with read-only consumers and runtime evidence navigation.': '托管 Skill 定义，包含只读消费方与运行时证据导航。',
  'Loaded skill not found.': '未找到已加载 Skill。',
  'The live persona detail response did not include this skill.': '实时 Persona 详情响应未包含此 Skill。',
  'Preview create request': '预览创建请求',
  'Skill content': 'Skill 内容',
  'Markdown-first content view with structured scope/when fields.': 'Markdown 优先的内容视图，包含结构化 scope/when 字段。',
  'No Skill body returned.': '未返回 Skill 正文。',
  'Create or save a draft to add Markdown content.': '创建或保存草稿以添加 Markdown 内容。',
  'No Markdown content.': '无 Markdown 内容。',
  'Draft / preview content': '草稿 / 预览内容',
  'Published content': '已发布内容',
  'published version': '已发布版本',
  'Only live persona skill snapshots are shown as runtime evidence.': '仅将实时 Persona Skill 快照展示为运行时证据。',
  Scope: '作用域',
  'Proxy evidence': 'Proxy 证据',
  'Runtime exposure': '运行时暴露',
  'Personas checked': '已检查 Persona',
  'Runtime-backed': '运行时支持',
  'Native MCPs': 'Native MCP 数',
  'Proxy MCPs': 'Proxy MCP 数',
  'Used by': '使用方',
  'Drawer gated': '抽屉保护',
  'Create, import, edit, prompt/policy, and runtime evidence open on demand': '创建、导入、编辑、Prompt/策略和运行时证据按需打开',
  'MCP Servers': 'MCP 服务',
  'Managed MCP definitions split into Native MCP connection configs and cc-connect Proxy MCP policy configs.': '托管 MCP 定义拆分为 Native MCP 连接配置和 cc-connect Proxy MCP 策略配置。',
  'Native + cc-connect Proxy MCPs': 'Native + cc-connect Proxy MCP',
  'No managed MCP definitions returned.': '未返回托管 MCP 定义。',
  'The management API returned an empty MCP server list.': '管理 API 返回了空 MCP 服务列表。',
  'Used-by evidence is read-only and aggregated from live Persona MCP snapshots; Persona assignment remains in Persona config.': '使用方证据只读，并由实时 Persona MCP 快照聚合；Persona assignment 保留在 Persona 配置中。',
  'Read-only Persona runtime snapshots': '只读 Persona 运行时快照',
  'MCP Presets': 'MCP 预设',
  'MCP preset registry': 'MCP 预设注册表',
  'Backend registry': '后端注册表',
  'Proxy presets': 'Proxy 预设',
  'Credential values': '凭据值',
  Redacted: '已脱敏',
  'Required bindings only': '仅必需绑定',
  Install: '安装',
  Available: '可用',
  'No MCP presets returned.': '未返回 MCP 预设。',
  'The backend registry returned an empty preset list.': '后端注册表返回了空预设列表。',
  'Definition summary': '定义摘要',
  'Proxy MCP policy model': 'Proxy MCP 策略模型',
  'Native MCP connection model': 'Native MCP 连接模型',
  'MCP-owned prompt, permission, audit, and visibility controls.': 'MCP 自有的 Prompt、权限、审计和可见性控制。',
  'MCP-owned connection, transport, command, URL, args, env, and secret refs.': 'MCP 自有的连接、transport、command、URL、args、env 和 secret refs。',
  'Secret refs': 'Secret refs',
  'Registry references only; token values are never shown or accepted.': '仅保留注册表引用；token 值永不展示或接收。',
  'Proxy rules': 'Proxy 规则',
  'Read-only policy evidence from the current MCP spec.': '来自当前 MCP spec 的只读策略证据。',
  'Live proxy rows': '实时 Proxy 行',
  'Runtime MCP proxy state from persona detail snapshots.': '来自 Persona 详情快照的运行时 MCP Proxy 状态。',
  Server: '服务',
  Proxy: 'Proxy',
  'Credential policy': '凭据策略',
  'Shadowed managed MCPs': '被遮蔽的托管 MCP',
  'Managed servers dropped by config-wins merge semantics.': '因配置优先生效的合并语义而被丢弃的托管服务。',
  Type: '类型',
  'Credential requirements': '凭据要求',
  'Action state': '操作状态',
  Reference: '引用',
  'key hidden': 'key 已隐藏',
  'Tool/schema visibility': '工具/schema 可见性',
  'Prompt injection': 'Prompt 注入',
  Permission: '权限',
  Consumer: '消费方',
  'Referenced version': '引用版本',
  'Runtime state': '运行时状态',
  Navigate: '导航',
  'Persona binding stays in Persona config; this table only links to owning config and runtime evidence.': 'Persona 绑定保留在 Persona config 中；此表只链接到所属配置和运行时证据。',
  'Proxy runtime evidence': 'Proxy 运行时证据',
  'Active proxy state, config precedence, last-call availability, audit health, and schema visibility.': '活动 Proxy 状态、配置优先级、最近调用可用性、审计健康和 schema 可见性。',
  'Persona/server': 'Persona/服务',
  'Active proxy state': '活动 Proxy 状态',
  'Shadowing / precedence': '遮蔽 / 优先级',
  'Last call': '最近调用',
  'Audit health': '审计健康',
  'Tool definitions': '工具定义',
  'Partial/read-only state.': '部分/只读状态。',
  'Partial data': '部分数据',
  'Redacted draft-vs-published diff. This is the only draft JSON drawer; View Config JSON remains the single full JSON tab.': '已脱敏的草稿与已发布版本 diff。这是唯一的草稿 JSON 抽屉；查看配置 JSON 仍是唯一完整 JSON 标签页。',
  Published: '已发布',
  Diff: 'Diff',
  'Row actions': '行操作',
  'Created managed MCP. Opening detail so the connection or proxy policy draft can be edited on demand.': '已创建托管 MCP，正在打开详情以按需编辑连接或 Proxy 策略草稿。',
  'Draft saved. Raw headers are scrubbed and sensitive env placeholders are redacted; use secret_refs for credentials.': '草稿已保存。原始 header 已清理，敏感 env 占位符已脱敏；凭据请使用 secret_refs。',
  'Import MCP preset': '导入 MCP 预设',
  'Proxy prompt / permission / audit policy': 'Proxy Prompt / 权限 / 审计策略',
  'MCP runtime evidence': 'MCP 运行时证据',
  'Edit MCP configuration': '编辑 MCP 配置',
  'Tools/MCP': '工具/MCP',
  'Tool Proxy': '工具 Proxy',
  'Runtime-backed MCP proxy visibility from live persona details. Credential values and tool schemas are not rendered.': '来自实时 Persona 详情的 MCP Proxy 运行时可见性。凭据值和工具 schema 不会渲染。',
  'MCP proxy runtime evidence': 'MCP Proxy 运行时证据',
  'Tool schemas': '工具 schema',
  'Proxy rows': 'Proxy 行',
  'Live MCP snapshots': '实时 MCP 快照',
  'Local proxies': '本地 Proxy',
  'No live MCP proxy rows returned.': '未返回实时 MCP Proxy 行。',
  'Tool proxy rows appear only when persona detail endpoints expose MCP runtime snapshots.': '只有 Persona 详情接口暴露 MCP 运行时快照时，才会显示工具 Proxy 行。',
  'Tool definitions are partial: current management APIs expose proxy servers and policy rule counts, not authoritative runtime tool schemas.': '工具定义是部分数据：当前管理 API 暴露 Proxy 服务和策略规则数量，而不是权威运行时工具 schema。',
  'No shadowed managed MCPs returned.': '未返回被遮蔽的托管 MCP。',
  'No live management API rows are available for this section.': '此区域没有可用的实时管理 API 行。',
  'MCP inventory': 'MCP 清单',
  'Default view stays list-first: filter definitions, inspect status, create/import from header drawers, and use row actions for details.': '默认视图保持列表优先：筛选定义、检查状态、通过页头抽屉创建/导入，并使用行操作查看详情。',
  'Filter MCPs': '筛选 MCP',
  'Name, mode, status, used by…': '名称、模式、状态、使用方…',
  'All modes': '全部模式',
  'All statuses': '全部状态',
  'Native MCP': 'Native MCP',
  'cc-connect Proxy MCP': 'cc-connect Proxy MCP',
  'Connection-first MCPs manage transport, command, URL, args, and secret refs from drawers or detail tabs.': '连接优先的 MCP 通过抽屉或详情标签管理 transport、command、URL、args 和 secret refs。',
  'No Native MCP definitions match the filters.': '没有符合筛选条件的 Native MCP 定义。',
  'Create or import a non-proxy MCP to manage native connection settings.': '创建或导入非 Proxy MCP 以管理 Native 连接设置。',
  'Prompt injection, permission policy, audit policy, and runtime evidence open on demand instead of occupying the list.': 'Prompt 注入、权限策略、审计策略和运行时证据按需打开，不占据列表默认视图。',
  'No Proxy MCP definitions match the filters.': '没有符合筛选条件的 Proxy MCP 定义。',
  'Create a cc-connect Proxy MCP or convert a Native definition when proxy policy is needed.': '需要 Proxy 策略时，请创建 cc-connect Proxy MCP 或转换 Native 定义。',
  'Lifecycle actions': '生命周期操作',
  Result: '结果',
  'Lifecycle controls': '生命周期控制',
  'Every mutation starts as a request preview; destructive actions require explicit confirmation.': '每个变更都从请求预览开始；破坏性操作需要显式确认。',
  'Status transition': '状态转换',
  'Status is previewed as a lifecycle PATCH request; draft status is backend-owned.': '状态变更会先预览为生命周期 PATCH 请求；草稿状态由后端管理。',
  'Backend supports read-only draft validation via POST /skills/{id}/validate.': '后端支持通过 POST /skills/{id}/validate 进行只读草稿校验。',
  'Backend supports structured draft preview via GET /skills/{id}/preview.': '后端支持通过 GET /skills/{id}/preview 进行结构化草稿预览。',
  'Backend creates immutable versions from the current validated draft.': '后端会基于当前已校验草稿创建不可变版本。',
  Validate: '校验',
  Preview: '预览',
  'Create version': '创建版本',
  'Publish latest': '发布最新版本',
  Activate: '启用',
  Disable: '停用',
  Archive: '归档',
  Delete: '删除',
  'Create shell, then edit Proxy policy': '创建 shell 后编辑 Proxy 策略',
  'Create shell, then edit Native connection': '创建 shell 后编辑 Native 连接',
  'Create only the managed MCP artifact. Connection and proxy-owned fields stay in the edit drawer/detail tabs.': '仅创建托管 MCP 工件。连接和 Proxy 自有字段保留在编辑抽屉/详情标签中。',
  '1. Define artifact': '1. 定义工件',
  'Name, display name, and description create the MCP shell.': '名称、显示名称和描述会创建 MCP shell。',
  '2. Edit draft': '2. 编辑草稿',
  'Open Prompt / policy to add prompt injection, permission, audit, secret refs, and runtime name.': '打开 Prompt / 策略以添加 Prompt 注入、权限、审计、secret refs 和运行时名称。',
  'Open Edit connection to add transport, command, URL, args, and secret refs.': '打开编辑连接以添加 transport、command、URL、args 和 secret refs。',
  '3. Review detail tabs': '3. 检查详情标签',
  'Use details for View Config JSON, draft diff, usage, and runtime evidence.': '通过详情查看配置 JSON、草稿 diff、使用情况和运行时证据。',
  'MCP shell fields': 'MCP shell 字段',
  'No Persona assignment happens here; the list will show read-only usage after configs reference it.': '这里不会进行 Persona assignment；配置引用后，列表会展示只读使用情况。',
  'Managed MCP definition': '托管 MCP 定义',
  'Create request preview': '创建请求预览',
  'Exact request preview is drawer-only; the default list never renders request previews.': '精确请求预览仅在抽屉中展示；默认列表不会渲染请求预览。',
  'Create MCP shell': '创建 MCP shell',
  'Create result': '创建结果',
  'Preset import wizard': '预设导入向导',
  'GitHub-style presets remain fast, but install runs through steps, preview, and confirmation inside this drawer.': 'GitHub 风格预设仍保持快速入口，但安装会在此抽屉内经过步骤、预览和确认。',
  '1. Choose preset': '1. 选择预设',
  'Select a backend registry preset; the default list only links to this wizard.': '选择后端注册表预设；默认列表只链接到此向导。',
  '2. Bind secret refs': '2. 绑定 secret refs',
  'Enter registry keys only. Secret values are not accepted or displayed.': '只输入注册表 key。Secret 值不会被接收或展示。',
  '3. Target project draft': '3. 目标项目草稿',
  'The backend install contract requires project_id and appends an artifact ref to that draft for later review/publish.': '后端安装契约需要 project_id，并会向该草稿追加工件引用以供后续检查/发布。',
  'Preset and bindings': '预设与绑定',
  'No preset description returned.': '未返回预设描述。',
  'Secret values are not accepted here; only existing registry refs are submitted.': '这里不接收 secret 值；只提交已有注册表引用。',
  'Install request preview': '安装请求预览',
  'Proxy MCP artifact + target project draft ref': 'Proxy MCP 工件 + 目标项目草稿引用',
  'Publishes Persona config': '发布 Persona 配置',
  'Preview and confirmation stay inside the drawer, not on the default MCP list.': '预览和确认保留在抽屉内，不显示在默认 MCP 列表上。',
  'I reviewed the preset import preview and target project draft impact.': '我已检查预设导入预览和目标项目草稿影响。',
  'Install preset': '安装预设',
  'Install result': '安装结果',
  'MCP edit drawer': 'MCP 编辑抽屉',
  'Usage is read-only and shown after live Persona snapshots reference this MCP.': '使用情况只读，并在实时 Persona 快照引用此 MCP 后展示。',
  'Current Proxy policy summary': '当前 Proxy 策略摘要',
  'Prompt injection, permission, audit policy, credential visibility, and schema visibility.': 'Prompt 注入、权限、审计策略、凭据可见性和 schema 可见性。',
  'Current proxy rules': '当前 Proxy 规则',
  'Prompt injection and permission/audit details are on demand.': 'Prompt 注入和权限/审计详情按需展示。',
  'MCP-owned transport, command, URL, args, runtime name, and secret refs.': 'MCP 自有的 transport、command、URL、args、运行时名称和 secret refs。',
  'Draft JSON / diff entry': '草稿 JSON / diff 入口',
  'Full draft JSON opens from the editor action; it is not the primary editing path.': '完整草稿 JSON 从编辑器操作打开；它不是主要编辑路径。',
  'Open Draft JSON + diff': '打开草稿 JSON + diff',
  'MCP lifecycle controls': 'MCP 生命周期控制',
  'Lifecycle actions are intentionally separate from structured draft editing and remain preview-gated.': '生命周期操作与结构化草稿编辑保持分离，并继续由预览保护。',
  'MCP lifecycle action': 'MCP 生命周期操作',
  'Validate draft': '校验草稿',
  'Lifecycle preview': '生命周期预览',
  'Destructive actions require typed confirmation. Run support is disabled until backend MCP lifecycle contracts exist.': '破坏性操作需要输入确认。在后端 MCP 生命周期契约存在前，运行支持保持禁用。',
  'Target MCP': '目标 MCP',
  'Lifecycle contract': '生命周期契约',
  'Separate from Save draft; backend MCP lifecycle preview/run endpoints are not exposed yet': '与保存草稿分离；后端尚未暴露 MCP 生命周期预览/运行接口',
  'Destructive guard': '破坏性保护',
  'Preview-only confirmation is sufficient for non-destructive actions': '非破坏性操作只需要预览确认',
  'Persona binding': 'Persona 绑定',
  'Not available from MCP pages': 'MCP 页面不可用',
  'Typed confirmation': '输入确认',
  'Non-destructive lifecycle actions still require preview before run.': '非破坏性生命周期操作在运行前仍需要预览。',
  'Type confirmation to enable when supported': '支持后输入确认即可启用',
  'Run unavailable: backend contract gap': '无法运行：后端契约缺口',
  'Backend contract gaps': '后端契约缺口',
  'These gaps are surfaced instead of folding lifecycle into raw JSON editing.': '这些缺口会显式展示，而不是把生命周期操作折叠进原始 JSON 编辑。',
  'Persona binding/install/unbind is intentionally read-only here; edit bindings in Persona config.': 'Persona 绑定/安装/解绑在此刻意保持只读；请在 Persona config 中编辑绑定。',
  'Backend destructive preview/confirmation endpoint gap: PATCH archive and DELETE execute directly; Console shows client-side request preview plus explicit confirmation.': '后端破坏性预览/确认接口存在缺口：PATCH archive 与 DELETE 会直接执行；Console 会显示客户端请求预览和显式确认。',
  'Metadata edit endpoint gap: backend supports create metadata and status changes, but not display name/description updates for existing Skill manifests.': 'Metadata 编辑接口存在缺口：后端支持创建 metadata 和状态变更，但不支持更新现有 Skill manifest 的显示名称/描述。',
  'No MCP-specific consumers endpoint; usage is aggregated from Persona detail snapshots and is read-only.': '没有 MCP 专用 consumers 接口；使用情况从 Persona 详情快照聚合，并且只读。',
  'No structured proxy last-call/audit-health endpoint; runtime evidence marks this as unavailable instead of inferring it from logs.': '没有结构化 Proxy 最近调用/审计健康接口；运行时证据会标记为不可用，而不是从日志推断。',
  'No authoritative tool/schema visibility endpoint for Proxy MCP; proxy rule visibility is shown as partial evidence.': 'Proxy MCP 没有权威工具/schema 可见性接口；Proxy 规则可见性会作为部分证据展示。',
  'Remove secret ref': '移除 secret ref',
  'Remove proxy rule': '移除 Proxy 规则',
  'Secret ref': 'Secret ref',
  'Add default param': '新增默认参数',
  Param: '参数',
  'JSON/value': 'JSON/值',
  'Latest transition preview does not allow this action.': '最新转换预览不允许此操作。',
  'Archived Skill artifacts are terminal in the backend contract.': '已归档 Skill 工件在后端契约中为终态。',
  'No draft revision returned by /skills/{id}.': '后端未从 /skills/{id} 返回草稿修订。',
  'No unpublished version is available to publish.': '没有可发布的未发布版本。',
  'No older version is available for rollback.': '没有可回滚的旧版本。',
  'Destructive status transition: backend archives the Skill after this request; review consumers first.': '破坏性状态转换：后端会在此请求后归档 Skill；请先检查消费方。',
  'Destructive action: backend DELETE archives the Skill and removes it from the name index; review consumers first.': '破坏性操作：后端 DELETE 会归档 Skill 并从名称索引移除；请先检查消费方。',
};

const zhCNFallbackReplacements: Array<[RegExp, string]> = [
  [/\bStructured\b/g, '结构化'],
  [/\bconfiguration\b/gi, '配置'],
  [/\bconfig\b/gi, '配置'],
  [/\beditor\b/gi, '编辑器'],
  [/\bfoundation\b/gi, '基础'],
  [/\bcreate\b/gi, '创建'],
  [/\bview\b/gi, '查看'],
  [/\bedit\b/gi, '编辑'],
  [/\bselect\b/gi, '选择'],
  [/\bopen\b/gi, '打开'],
  [/\binitial\b/gi, '初始'],
  [/\bstatus\b/gi, '状态'],
  [/\bboundary\b/gi, '边界'],
  [/\bmanifest\b/gi, '清单'],
  [/\bfield(s)?\b/gi, '字段'],
  [/\brequest\b/gi, '请求'],
  [/\bpreview\b/gi, '预览'],
  [/\bconfirmation\b/gi, '确认'],
  [/\bdisplay name\b/gi, '显示名称'],
  [/\bactive\b/gi, '当前'],
  [/\bprovider\b/gi, 'Provider'],
  [/\bmodel\b/gi, '模型'],
  [/\bruntime\b/gi, '运行时'],
  [/\bidentity\b/gi, '身份'],
  [/\breasoning\b/gi, '推理'],
  [/\bbudget(s)?\b/gi, '预算'],
  [/\bcontrol(s)?\b/gi, '控制'],
  [/\boption(s)?\b/gi, '选项'],
  [/\bexisting\b/gi, '现有'],
  [/\bread-only\b/gi, '只读'],
  [/\bbackend\b/gi, '后端'],
  [/\bendpoint\b/gi, '接口'],
  [/\bgap\b/gi, '缺口'],
  [/\btag(s)?\b/gi, '标签'],
  [/\bmetadata\b/gi, '元数据'],
  [/\bevidence\b/gi, '证据'],
  [/\bbody\b/gi, '正文'],
  [/\bcontent\b/gi, '内容'],
  [/\bscope\b/gi, '作用域'],
  [/\bplatform(s)?\b/gi, '平台'],
  [/\bagent(s)?\b/gi, 'Agent'],
  [/\bbinding(s)?\b/gi, '绑定'],
  [/\bdefault\b/gi, '默认'],
  [/\boverride\b/gi, 'override'],
  [/\bremove\b/gi, '移除'],
  [/\badd\b/gi, '新增'],
  [/\brule(s)?\b/gi, '规则'],
  [/\bpolicy\b/gi, '策略'],
  [/\bpermission\b/gi, '权限'],
  [/\baudit\b/gi, '审计'],
  [/\bproxy\b/gi, 'Proxy'],
  [/\bconnection\b/gi, '连接'],
  [/\btransport\b/gi, 'transport'],
  [/\bcommand\b/gi, 'command'],
  [/\bargs\b/gi, 'args'],
  [/\bsecret refs\b/gi, 'secret refs'],
  [/\bvisibility\b/gi, '可见性'],
  [/\brequired\b/gi, '必需'],
  [/\boptional\b/gi, '可选'],
  [/\btarget\b/gi, '目标'],
  [/\bregistry key\b/gi, '注册表 key'],
  [/\bcurrent\b/gi, '当前'],
  [/\bsummary\b/gi, '摘要'],
  [/\bdescription\b/gi, '描述'],
  [/\bmessage\b/gi, '消息'],
  [/\bvalue\b/gi, '值'],
  [/\bplaceholder\b/gi, '占位符'],
  [/\bempty\b/gi, '空'],
];

export function translateVisibleCopy(value: string, locale: LocaleCode): string {
  if (locale !== 'zh-CN') return value;

  const exact = zhCNVisibleCopy[value];
  if (exact) return exact;

  if (value.startsWith('Export started: ')) return `已开始导出：${value.slice('Export started: '.length)}`;
  if (value.startsWith('Heartbeat ')) return `心跳 ${value.slice('Heartbeat '.length)}`;
  if (value.startsWith('Configured ref #')) return `已配置引用 #${value.slice('Configured ref #'.length)}`;
  if (value.startsWith('audit rows ')) return `审计行 ${value.slice('audit rows '.length)}`;
  if (value.startsWith('Used by: ')) return `使用方：${value.slice('Used by: '.length)}`;
  if (value.endsWith(': Not Found')) return `${translateVisibleCopy(value.slice(0, -': Not Found'.length), locale)}：未找到`;
  if (value.endsWith(' live')) return `${value.slice(0, -' live'.length)} 实时`;
  if (value.endsWith(' artifact refs')) return `${value.slice(0, -' artifact refs'.length)} 个工件引用`;
  if (value.startsWith('Created ')) return `创建时间 ${value.slice('Created '.length)}`;
  if (value.startsWith('published ') && value.includes(' / current ')) {
    return value.replace('published ', '已发布 ').replace(' / current ', ' / 当前 ');
  }
  if (value.startsWith('No ') && value.endsWith(' recorded.')) return `未记录 ${value.slice(3, -10)}。`;
  if (value.startsWith('Confirm and send ')) return `确认并发送 ${translateVisibleCopy(value.slice('Confirm and send '.length), locale)}`;
  if (value.startsWith('Applying ') && value.endsWith('…')) return `正在执行 ${translateVisibleCopy(value.slice('Applying '.length, -1), locale)}…`;
  if (value.endsWith(' complete. Evidence refreshed.')) return `${translateVisibleCopy(value.slice(0, -' complete. Evidence refreshed.'.length), locale)}已完成，证据已刷新。`;
  if (value.endsWith(' completed. Live evidence refreshed.')) return `${translateVisibleCopy(value.slice(0, -' completed. Live evidence refreshed.'.length), locale)}已完成，实时证据已刷新。`;

  const skillLifecycleDraftActionMatch = value.match(/^(Save|Validate|Preview) draft$/);
  if (skillLifecycleDraftActionMatch) {
    const action = skillLifecycleDraftActionMatch[1] === 'Save' ? '保存' : skillLifecycleDraftActionMatch[1] === 'Validate' ? '校验' : '预览';
    return `${action}草稿`;
  }

  const supportedStatusTransitionMatch = value.match(/^Backend supports (.+) → (.+) status transition\.$/);
  if (supportedStatusTransitionMatch) {
    return `后端支持 ${translateVisibleCopy(supportedStatusTransitionMatch[1], locale)} → ${translateVisibleCopy(supportedStatusTransitionMatch[2], locale)} 状态转换。`;
  }

  const unsupportedStatusTransitionMatch = value.match(/^Backend does not support (.+) → (.+) transition\.$/);
  if (unsupportedStatusTransitionMatch) {
    return `后端不支持 ${translateVisibleCopy(unsupportedStatusTransitionMatch[1], locale)} → ${translateVisibleCopy(unsupportedStatusTransitionMatch[2], locale)} 转换。`;
  }

  const publishesSkillVersionMatch = value.match(/^Publishes Skill artifact version (v\d+); Persona consumers remain unchanged\.$/);
  if (publishesSkillVersionMatch) return `发布 Skill 工件版本 ${publishesSkillVersionMatch[1]}；Persona 消费方保持不变。`;

  const rollbackVersionMatch = value.match(/^Rolls the published pointer back to (v\d+); this does not unbind any Persona\.$/);
  if (rollbackVersionMatch) return `将已发布指针回滚到 ${rollbackVersionMatch[1]}；不会解绑任何 Persona。`;

  const sentMatch = value.match(/^(.+) sent successfully\. Refreshing projection may take one reload\.$/);
  if (sentMatch) return `${translateVisibleCopy(sentMatch[1], locale)} 已发送成功。刷新投影可能需要重新加载一次。`;

  const requiredTextMatch = value.match(/^Required text: (.+)$/);
  if (requiredTextMatch) return `必填文本：${requiredTextMatch[1]}`;

  const typeConfirmationMatch = value.match(/^Type "(.+)" before this action can run once backend support exists$/);
  if (typeConfirmationMatch) return `后端支持后，需先输入“${typeConfirmationMatch[1]}”才能运行此操作`;

  const transitionMatch = value.match(/^Current (.+) → target (.+)$/);
  if (transitionMatch) return `当前 ${transitionMatch[1]} → 目标 ${transitionMatch[2]}`;

  if (/\b(draft|published|runtime|loaded):/.test(value) && value.includes(' · ')) {
    return value
      .replace(/\bdraft:/g, '草稿:')
      .replace(/\bpublished:/g, '已发布:')
      .replace(/\bruntime:/g, '运行时:')
      .replace(/\bloaded:/g, '已加载:')
      .replace(/\babsent\b/g, '缺失')
      .replace(/\benabled\b/g, '启用')
      .replace(/\bdisabled\b/g, '禁用');
  }

  const fallback = translateWithFallbackReplacements(value);
  if (fallback !== value) return fallback;

  return value;
}

function translateWithFallbackReplacements(value: string) {
  if (!/[A-Za-z]/.test(value) || /^[/#]/.test(value) || /^[A-Z_]+$/.test(value)) return value;
  let translated = value;
  for (const [pattern, replacement] of zhCNFallbackReplacements) {
    translated = translated.replace(pattern, replacement);
  }
  return /[\u4e00-\u9fff]/.test(translated) ? translated : value;
}

export function translateVisibleNode(value: ReactNode, locale: LocaleCode) {
  return typeof value === 'string' ? translateVisibleCopy(value, locale) : value;
}

export function useVisibleCopy() {
  const { locale } = useI18n();
  return useCallback((value: string) => translateVisibleCopy(value, locale), [locale]);
}
